import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '@/shared/api/client';
import type {
  BankAccount,
  BrokerChannelType,
  BrokerOnboardingInput,
  BrokerOnboardingResult,
  BrokerRegistrationCheck,
  FinancialContact,
} from '@/shared/api/resources';
import { formatDocument } from '@/shared/lib/format';
import { AddressFields } from '@/shared/ui/AddressFields';
import { Alert } from '@/shared/ui/Alert';
import { BankCodeSelect } from '@/shared/ui/BankCodeSelect';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { Icon } from '@/shared/ui/Icon';
import { Select } from '@/shared/ui/Select';
import { Stepper, type Step } from '@/shared/ui/Stepper';
import { useStepper } from '@/shared/ui/useStepper';

import {
  aplicaDadosDaReceita,
  SENHA_MINIMA_DO_ASSISTENTE,
  confereEtapaDoAssistente,
  dadosIniciaisDoAssistente,
  etapaDoCampoDoAssistente,
  etapasDoAssistente,
  MENSAGEM_TODOS_OS_CAMPOS,
  novaPessoaDoAssistente,
  pedidoDoAssistente,
  rotulosDasEtapas,
  soDigitosDoAssistente,
  type AcessoDoAssistente,
  type ContextoDoAssistente,
  type CorretoraDoAssistente,
  type DadosDoAssistente,
  type ErrosDoAssistente,
  type EtapaDoAssistente,
  type PessoaDoAssistente,
} from './assistenteDoCorretor';
import { AvisoDaSusep } from './AvisoDaSusep';
import { dicaDadosBancarios } from './contaBancaria';
import { susepRecusouEsteCnpj } from './triagemSusep';

/** Perfil da corretora que quem cadastra pode conceder. */
export interface PerfilDoAssistente {
  id: string;
  name: string;
  key?: string | undefined;
  is_default?: boolean | undefined;
}

export interface BrokerWizardProps<R = BrokerOnboardingResult> {
  /** Canais oferecidos no passo Dados da Corretora. */
  canais: BrokerChannelType[];
  /** Perfis da corretora para o administrador e os usuarios. */
  perfis: PerfilDoAssistente[];
  /** corretores.dados_bancarios.update. Sem ela, a conta fica para depois. */
  podeAlterarConta: boolean;
  /** Convidar e conceder perfil. Sem elas, a corretora nasce sem pessoas. */
  podeConvidar: boolean;
  /** Passo Buscar CNPJ. */
  verificarCnpj: (cnpj: string) => Promise<BrokerRegistrationCheck>;
  /**
   * "Concluir cadastro". Recebe tambem os dados da tela, para o cadastro publico levar o
   * passo Dados do Usuario com a senha (ADR 0095).
   */
  concluir: (pedido: BrokerOnboardingInput, dados: DadosDoAssistente) => Promise<R>;
  onConcluido: (resultado: R) => void;
  /** "Voltar" no primeiro passo. */
  onCancelar: () => void;
  /** Endereco do cadastro da corretora ja cadastrada. Sem ele, so o aviso. */
  caminhoDaCorretora?: ((brokerId: string) => string) | undefined;
  /** CNPJ ja conhecido, como no autocadastro que chega pelo endereco. */
  cnpjInicial?: string | undefined;
  /** Dados ja preenchidos, como o rascunho do autocadastro (P-ACE-2). */
  dadosIniciais?: DadosDoAssistente | undefined;
  /**
   * Bancos ja carregados. O autocadastro publico recebe a lista com o pedido, porque
   * sem sessao nao le o catalogo.
   */
  bancos?: { code: string; name: string }[] | undefined;
  /** Chamado a cada etapa conferida, com o pedido ate ali (o rascunho do autocadastro). */
  aoAvancar?: ((pedido: BrokerOnboardingInput) => void) | undefined;
  /**
   * Cadastro publico com acesso na hora (ADR 0095): o passo Dados da Corretora fica sem
   * o administrador, e o passo Dados do Usuario pede nome, e-mail, CPF, telefone, senha
   * e confirmacao, com a ciencia do aviso de privacidade.
   */
  acessoImediato?: { avisoDePrivacidade: ReactNode } | undefined;
}

const tiposDeConta = [
  { value: 'corrente', label: 'Conta corrente' },
  { value: 'poupanca', label: 'Conta poupanca' },
];

/**
 * Perfil sugerido para o administrador do portal: o Administrador da Corretora, que
 * define os usuarios dela. O OnPoint nao mostra o perfil; aqui ele aparece preenchido
 * para que a empresa que mudou os perfis escolha outro.
 */
function perfilDoAdministrador(perfis: PerfilDoAssistente[]): string {
  return (
    (perfis.find((perfil) => perfil.key === 'corretor_parceiro_admin') ?? perfis[0])
      ?.id ?? ''
  );
}

/** Perfil sugerido para o usuario: o padrao da corretora, ou o Corretor Parceiro. */
function perfilDoUsuario(perfis: PerfilDoAssistente[]): string {
  return (
    (
      perfis.find((perfil) => perfil.is_default) ??
      perfis.find((perfil) => perfil.key === 'corretor_parceiro')
    )?.id ?? ''
  );
}

/**
 * Assistente de cadastro de corretor, com os quatro passos do OnPoint: Buscar CNPJ,
 * Dados da Corretora (com o administrador do portal), Dados Bancarios (com o
 * responsavel financeiro) e Dados do Usuario, e os botoes Voltar, Proxima etapa e
 * Concluir cadastro.
 *
 * O componente nao chama a API: recebe a conferencia do CNPJ e o "Concluir cadastro"
 * de quem o usa. Assim o mesmo assistente serve a seguradora, em `/corretores/novo`,
 * e o autocadastro da corretora (P-ACE-2), que chega sem sessao e com outra rota. As
 * regras do formulario ficam em `assistenteDoCorretor.ts`.
 *
 * O servidor confere tudo de novo e grava numa transacao: a conferencia aqui existe
 * para a pessoa saber o que falta antes de chegar ao fim, como no OnPoint.
 */
export function BrokerWizard<R = BrokerOnboardingResult>({
  canais,
  perfis,
  podeAlterarConta,
  podeConvidar,
  verificarCnpj,
  concluir,
  onConcluido,
  onCancelar,
  caminhoDaCorretora,
  cnpjInicial,
  dadosIniciais,
  bancos,
  aoAvancar,
  acessoImediato,
}: BrokerWizardProps<R>) {
  const passo = useStepper(etapasDoAssistente);
  const [dados, setDados] = useState<DadosDoAssistente>(
    () => dadosIniciais ?? dadosIniciaisDoAssistente(cnpjInicial ?? ''),
  );
  const [erros, setErros] = useState<ErrosDoAssistente>({});
  const [falha, setFalha] = useState<string | null>(null);
  const [conferencia, setConferencia] = useState<BrokerRegistrationCheck | null>(null);
  const [concluidas, setConcluidas] = useState<ReadonlySet<EtapaDoAssistente>>(
    new Set(),
  );
  const [ocupado, setOcupado] = useState(false);

  const canal = canais.find((item) => item.id === dados.corretora.channel_type_id);
  const contexto: ContextoDoAssistente = {
    podeAlterarConta,
    podeConvidar,
    canalExigeCodigos: canal?.requires_insurer_codes ?? false,
    haCanais: canais.length > 0,
    acessoImediato: acessoImediato !== undefined,
  };

  // O perfil sugerido entra quando a pessoa nao escolheu outro. Calculado, e nao
  // guardado, porque a lista de perfis chega depois do assistente abrir.
  function comPerfisSugeridos(atual: DadosDoAssistente): DadosDoAssistente {
    const admin = perfilDoAdministrador(perfis);
    const usuario = perfilDoUsuario(perfis);
    return {
      ...atual,
      administrador: {
        ...atual.administrador,
        role_id: atual.administrador.role_id || admin,
      },
      usuarios: atual.usuarios.map((pessoa) => ({
        ...pessoa,
        role_id: pessoa.role_id || usuario,
      })),
    };
  }
  const completos = comPerfisSugeridos(dados);

  // A corretora que nao passou na triagem da SUSEP (ADR 0095) nao segue: as etapas
  // seguintes ficam travadas como antes do "Iniciar cadastro".
  const cnpjConferido =
    conferencia !== null &&
    !conferencia.already_registered &&
    conferencia.susep?.proceed !== false &&
    conferencia.document === soDigitosDoAssistente(dados.cnpj);
  const susepRecusou = susepRecusouEsteCnpj(conferencia?.susep);

  const etapas: Step[] = etapasDoAssistente.map((chave) => ({
    key: chave,
    label: rotulosDasEtapas[chave],
    state: Object.keys(erros).some((campo) => etapaDoCampoDoAssistente(campo) === chave)
      ? 'erro'
      : concluidas.has(chave)
        ? 'concluida'
        : undefined,
    disabledReason:
      chave !== 'cnpj' && !cnpjConferido ? 'Inicie o cadastro pelo CNPJ.' : undefined,
  }));

  // A recusa do servidor pode apontar o convite inteiro (o teto, em "portal_admin") ou
  // a lista de perfis ("role_ids"): mexer no campo limpa as duas formas.
  function limpaErro(...campos: string[]) {
    setErros((atuais) => {
      if (!campos.some((campo) => campo in atuais)) return atuais;
      const restantes = { ...atuais };
      for (const campo of campos) delete restantes[campo];
      return restantes;
    });
  }

  function setCorretora(campo: keyof CorretoraDoAssistente, valor: string) {
    setDados((atual) => ({
      ...atual,
      corretora: { ...atual.corretora, [campo]: valor },
    }));
    limpaErro(campo);
  }

  function setAdministrador(campo: keyof PessoaDoAssistente, valor: string) {
    setDados((atual) => ({
      ...atual,
      administrador: { ...atual.administrador, [campo]: valor },
    }));
    limpaErro(`portal_admin.${campo}`, 'portal_admin.role_ids', 'portal_admin');
  }

  function setConta(campo: keyof BankAccount, valor: string) {
    setDados((atual) => ({ ...atual, conta: { ...atual.conta, [campo]: valor } }));
    limpaErro(`bank_account.${campo}`, 'bank_account');
  }

  function setResponsavel(campo: keyof FinancialContact, valor: string) {
    setDados((atual) => ({
      ...atual,
      responsavel: { ...atual.responsavel, [campo]: valor },
    }));
    limpaErro(`financial_contact.${campo}`);
  }

  function setAcesso(campo: keyof AcessoDoAssistente, valor: string | boolean) {
    setDados((atual) => ({ ...atual, acesso: { ...atual.acesso, [campo]: valor } }));
    limpaErro(campo === 'ciente' ? 'privacy_acknowledged' : `user.${campo}`);
  }

  function setUsuario(indice: number, campo: keyof PessoaDoAssistente, valor: string) {
    setDados((atual) => ({
      ...atual,
      usuarios: atual.usuarios.map((pessoa, i) =>
        i === indice ? { ...pessoa, [campo]: valor } : pessoa,
      ),
    }));
    limpaErro(
      `users[${indice}].${campo}`,
      `users[${indice}].role_ids`,
      `users[${indice}]`,
    );
  }

  function removeUsuario(indice: number) {
    setDados((atual) => ({
      ...atual,
      usuarios: atual.usuarios.filter((_, i) => i !== indice),
    }));
    // Os indices dos que ficam mudam: os erros dos usuarios saem todos.
    setErros((atuais) =>
      Object.fromEntries(
        Object.entries(atuais).filter(([campo]) => !campo.startsWith('users')),
      ),
    );
  }

  /** Confere a etapa e troca os erros dela pelos novos. */
  function confere(etapa: EtapaDoAssistente): boolean {
    const novos = confereEtapaDoAssistente(etapa, completos, contexto);
    setErros((atuais) => ({
      ...Object.fromEntries(
        Object.entries(atuais).filter(
          ([campo]) => etapaDoCampoDoAssistente(campo) !== etapa,
        ),
      ),
      ...novos,
    }));
    const ok = Object.keys(novos).length === 0;
    setConcluidas((atuais) => {
      const proximas = new Set(atuais);
      if (ok) proximas.add(etapa);
      else proximas.delete(etapa);
      return proximas;
    });
    return ok;
  }

  /** Recusa do servidor: a mensagem no campo e a etapa dele aberta. */
  function mostraRecusa(erro: unknown) {
    const mensagem =
      erro instanceof Error ? erro.message : 'Nao foi possivel concluir o cadastro.';
    const campo =
      erro instanceof ApiError && typeof erro.details?.field === 'string'
        ? erro.details.field
        : null;
    setFalha(mensagem);
    if (campo) {
      setErros((atuais) => ({ ...atuais, [campo]: mensagem }));
      passo.goTo(etapaDoCampoDoAssistente(campo));
    }
  }

  async function iniciarCadastro() {
    setFalha(null);
    setConferencia(null);
    if (!confere('cnpj')) return;
    setOcupado(true);
    try {
      const resposta = await verificarCnpj(soDigitosDoAssistente(dados.cnpj));
      setConferencia(resposta);
      if (resposta.already_registered) return;
      // Triagem da SUSEP (ADR 0095): a corretora que nao esta apta fica no passo, com a
      // mensagem; a apta segue com o numero do registro preenchido.
      if (resposta.susep && !resposta.susep.proceed) return;
      const receita = resposta.company_lookup.data;
      const numeroSusep =
        resposta.susep?.status === 'apta' ? resposta.susep.susep_number : '';
      setDados((atual) => {
        const corretora =
          resposta.company_lookup.status === 'encontrada' && receita
            ? aplicaDadosDaReceita(atual.corretora, receita)
            : atual.corretora;
        return {
          ...atual,
          corretora: numeroSusep
            ? { ...corretora, susep_code: numeroSusep }
            : corretora,
        };
      });
      passo.next();
    } catch (erro) {
      mostraRecusa(erro);
    } finally {
      setOcupado(false);
    }
  }

  function proximaEtapa() {
    setFalha(null);
    if (!confere(passo.current)) {
      setFalha(MENSAGEM_TODOS_OS_CAMPOS);
      return;
    }
    aoAvancar?.(pedidoDoAssistente(completos, contexto));
    passo.next();
  }

  function voltar() {
    setFalha(null);
    if (passo.isFirst) onCancelar();
    else passo.back();
  }

  async function concluirCadastro() {
    setFalha(null);
    // Todas as etapas de novo: a pessoa pode ter voltado e mudado algo.
    for (const etapa of etapasDoAssistente) {
      if (!confere(etapa)) {
        passo.goTo(etapa);
        setFalha(MENSAGEM_TODOS_OS_CAMPOS);
        return;
      }
    }
    setOcupado(true);
    try {
      const resultado = await concluir(
        pedidoDoAssistente(completos, contexto),
        completos,
      );
      onConcluido(resultado);
    } catch (erro) {
      mostraRecusa(erro);
    } finally {
      setOcupado(false);
    }
  }

  const corretoraJaCadastrada = conferencia?.already_registered ? conferencia : null;

  return (
    <div className="flex flex-col gap-5">
      <Stepper
        steps={etapas}
        currentKey={passo.current}
        onStepClick={(chave) => {
          setFalha(null);
          passo.goTo(chave as EtapaDoAssistente);
        }}
        label="Etapas do cadastro de corretor"
      />

      {falha ? (
        <Alert tone="danger" title="Confira o cadastro">
          {falha}
        </Alert>
      ) : null}

      <Card>
        <div className="flex flex-col gap-5 p-5">
          {passo.current === 'cnpj' ? (
            <EtapaCnpj
              cnpj={dados.cnpj}
              erro={erros.document}
              onChange={(valor) => {
                setDados((atual) => ({ ...atual, cnpj: valor }));
                setConferencia(null);
                limpaErro('document');
              }}
              jaCadastrada={corretoraJaCadastrada}
              caminhoDaCorretora={caminhoDaCorretora}
              susep={conferencia?.susep}
            />
          ) : null}

          {passo.current === 'corretora' ? (
            <EtapaCorretora
              cnpj={dados.cnpj}
              corretora={dados.corretora}
              administrador={completos.administrador}
              conferencia={conferencia}
              canais={canais}
              perfis={perfis}
              contexto={contexto}
              erros={erros}
              onCorretora={setCorretora}
              onEndereco={(endereco) =>
                setDados((atual) => ({
                  ...atual,
                  corretora: { ...atual.corretora, address: endereco },
                }))
              }
              onAdministrador={setAdministrador}
            />
          ) : null}

          {passo.current === 'bancarios' ? (
            <EtapaBancarios
              conta={dados.conta}
              responsavel={dados.responsavel}
              podeAlterarConta={podeAlterarConta}
              bancos={bancos}
              erros={erros}
              onConta={setConta}
              onResponsavel={setResponsavel}
            />
          ) : null}

          {passo.current === 'usuarios' && acessoImediato ? (
            <EtapaAcesso
              acesso={dados.acesso}
              erros={erros}
              avisoDePrivacidade={acessoImediato.avisoDePrivacidade}
              onAcesso={setAcesso}
            />
          ) : null}

          {passo.current === 'usuarios' && !acessoImediato ? (
            <EtapaUsuarios
              usuarios={completos.usuarios}
              perfis={perfis}
              podeConvidar={podeConvidar}
              erros={erros}
              onUsuario={setUsuario}
              onAdicionar={() =>
                setDados((atual) => ({
                  ...atual,
                  usuarios: [...atual.usuarios, novaPessoaDoAssistente()],
                }))
              }
              onRemover={removeUsuario}
            />
          ) : null}
        </div>

        <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-4">
          <Button variant="secondary" onClick={voltar} disabled={ocupado}>
            Voltar
          </Button>
          {passo.current === 'cnpj' ? (
            <Button
              loading={ocupado}
              disabled={susepRecusou}
              onClick={() => void iniciarCadastro()}
            >
              Iniciar cadastro
            </Button>
          ) : passo.isLast ? (
            <Button loading={ocupado} onClick={() => void concluirCadastro()}>
              Concluir cadastro
            </Button>
          ) : (
            <Button onClick={proximaEtapa}>Proxima etapa</Button>
          )}
        </footer>
      </Card>
    </div>
  );
}

/** Bloco de uma etapa, com o titulo e a explicacao do OnPoint. */
function Secao({
  titulo,
  descricao,
  children,
}: {
  titulo: string;
  descricao?: string | undefined;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4">
      <header>
        <h2 className="text-sm font-semibold text-ink-900">{titulo}</h2>
        {descricao ? <p className="mt-0.5 text-xs text-ink-500">{descricao}</p> : null}
      </header>
      {children}
    </section>
  );
}

function EtapaCnpj({
  cnpj,
  erro,
  onChange,
  jaCadastrada,
  caminhoDaCorretora,
  susep,
}: {
  cnpj: string;
  erro: string | undefined;
  onChange: (valor: string) => void;
  jaCadastrada: BrokerRegistrationCheck | null;
  caminhoDaCorretora: ((brokerId: string) => string) | undefined;
  susep: BrokerRegistrationCheck['susep'];
}) {
  return (
    <Secao titulo="Digite o CNPJ da corretora">
      <div className="max-w-sm">
        <Field
          label="CNPJ"
          placeholder="Digite o CNPJ"
          inputMode="numeric"
          value={cnpj}
          error={erro}
          onChange={(evento) => onChange(evento.target.value)}
        />
      </div>
      {jaCadastrada ? (
        <Alert
          tone="danger"
          title="Erro! A corretora ja encontra-se cadastrada no sistema!"
        >
          {jaCadastrada.broker ? (
            <>
              {jaCadastrada.broker.legal_name}, CNPJ{' '}
              {formatDocument(jaCadastrada.document)}.{' '}
              {caminhoDaCorretora ? (
                <Link
                  className="font-medium underline"
                  to={caminhoDaCorretora(jaCadastrada.broker.id)}
                >
                  Abrir cadastro
                </Link>
              ) : null}
            </>
          ) : (
            `CNPJ ${formatDocument(jaCadastrada.document)}.`
          )}
        </Alert>
      ) : null}
      {!jaCadastrada && susep && !susep.proceed ? (
        <AvisoDaSusep triagem={susep} />
      ) : null}
    </Secao>
  );
}

function AvisoDaReceita({
  conferencia,
}: {
  conferencia: BrokerRegistrationCheck | null;
}) {
  const consulta = conferencia?.company_lookup;
  if (!consulta) return null;
  if (consulta.status === 'encontrada') {
    // A mensagem do servidor diz a fonte e a data: "Dados da Receita Federal pela
    // BrasilAPI em 27/10/2026." (ADR 0092). So o campo vazio recebeu o dado.
    return (
      <Alert tone="success">
        {consulta.message ||
          'Dados de cadastro da Corretora obtidos automaticamente da Receita Federal.'}{' '}
        Os campos ja preenchidos foram mantidos; confira antes de concluir.
      </Alert>
    );
  }
  return (
    <Alert tone={consulta.status === 'nao_configurada' ? 'info' : 'warning'}>
      {consulta.message ||
        'A consulta a Receita Federal nao esta configurada. Preencha os dados da corretora.'}
    </Alert>
  );
}

function EtapaCorretora({
  cnpj,
  corretora,
  administrador,
  conferencia,
  canais,
  perfis,
  contexto,
  erros,
  onCorretora,
  onEndereco,
  onAdministrador,
}: {
  cnpj: string;
  corretora: CorretoraDoAssistente;
  administrador: PessoaDoAssistente;
  conferencia: BrokerRegistrationCheck | null;
  canais: BrokerChannelType[];
  perfis: PerfilDoAssistente[];
  contexto: ContextoDoAssistente;
  erros: ErrosDoAssistente;
  onCorretora: (campo: keyof CorretoraDoAssistente, valor: string) => void;
  onEndereco: (endereco: CorretoraDoAssistente['address']) => void;
  onAdministrador: (campo: keyof PessoaDoAssistente, valor: string) => void;
}) {
  const canal = canais.find((item) => item.id === corretora.channel_type_id);
  return (
    <>
      <Secao titulo="Dados da Corretora">
        <AvisoDaSusep triagem={conferencia?.susep} />
        <AvisoDaReceita conferencia={conferencia} />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="CNPJ"
            value={formatDocument(soDigitosDoAssistente(cnpj))}
            readOnly
          />
          <Field
            label="Razao Social"
            value={corretora.legal_name}
            error={erros.legal_name}
            onChange={(evento) => onCorretora('legal_name', evento.target.value)}
          />
          <Field
            label="Numero SUSEP"
            value={corretora.susep_code}
            error={erros.susep_code}
            onChange={(evento) => onCorretora('susep_code', evento.target.value)}
          />
          <Field
            label="Inscricao Municipal"
            placeholder="Digite a inscricao municipal"
            maxLength={30}
            value={corretora.municipal_registration}
            error={erros.municipal_registration}
            onChange={(evento) =>
              onCorretora('municipal_registration', evento.target.value)
            }
          />
          <Select
            label="Canal"
            placeholder={
              contexto.haCanais ? 'Selecione o canal' : 'Nenhum canal cadastrado'
            }
            options={canais.map((item) => ({ value: item.id, label: item.name }))}
            value={corretora.channel_type_id}
            error={erros.channel_type_id}
            hint={canal?.description || undefined}
            onChange={(evento) => onCorretora('channel_type_id', evento.target.value)}
          />
          <Field
            label="Recadastramento SUSEP"
            type="date"
            value={corretora.reregistered_on}
            error={erros.reregistered_on}
            onChange={(evento) => onCorretora('reregistered_on', evento.target.value)}
          />
        </div>
        {contexto.canalExigeCodigos ? (
          <div className="flex flex-col gap-3 rounded-md border border-line p-4">
            <p className="text-xs text-ink-600">
              Quando o canal escolhido pede os codigos da seguradora, os campos a seguir
              devem ser preenchidos:
            </p>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field
                label="Codigo do corretor"
                placeholder="Digite o codigo"
                value={corretora.insurer_broker_code}
                error={erros.insurer_broker_code}
                onChange={(evento) =>
                  onCorretora('insurer_broker_code', evento.target.value)
                }
              />
              <Field
                label="Codigo da sucursal"
                placeholder="Digite o codigo"
                value={corretora.insurer_branch_code}
                error={erros.insurer_branch_code}
                onChange={(evento) =>
                  onCorretora('insurer_branch_code', evento.target.value)
                }
              />
              <Field
                label="Codigo do contrato"
                placeholder="Digite o codigo"
                value={corretora.insurer_contract_code}
                error={erros.insurer_contract_code}
                onChange={(evento) =>
                  onCorretora('insurer_contract_code', evento.target.value)
                }
              />
            </div>
          </div>
        ) : null}
      </Secao>

      <Secao
        titulo="Dados complementares"
        descricao="Os dados que a consulta a Receita Federal traria. Opcionais: a ficha do corretor recebe o resto depois."
      >
        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label="Nome fantasia"
            value={corretora.trade_name}
            error={erros.trade_name}
            onChange={(evento) => onCorretora('trade_name', evento.target.value)}
          />
          <Field
            label="E-mail da corretora"
            type="email"
            value={corretora.email}
            error={erros.email}
            onChange={(evento) => onCorretora('email', evento.target.value)}
          />
          <Field
            label="Telefone da corretora"
            value={corretora.phone}
            error={erros.phone}
            onChange={(evento) => onCorretora('phone', evento.target.value)}
          />
        </div>
        <AddressFields
          legend="Endereco"
          value={corretora.address}
          onChange={onEndereco}
        />
      </Secao>

      {contexto.acessoImediato ? null : (
        <Secao
          titulo="Dados do administrador do Portal"
          descricao="Dados da pessoa fisica que tera autonomia para definir novos usuarios na Plataforma Digital."
        >
          {contexto.podeConvidar ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Nome do administrador"
                placeholder="Digite o nome do administrador"
                value={administrador.name}
                error={erros['portal_admin.name']}
                onChange={(evento) => onAdministrador('name', evento.target.value)}
              />
              <Field
                label="E-mail"
                type="email"
                placeholder="Digite o e-mail"
                value={administrador.email}
                error={erros['portal_admin.email']}
                onChange={(evento) => onAdministrador('email', evento.target.value)}
              />
              <Field
                label="Celular"
                placeholder="Digite o celular"
                hint="Celular para autenticacao."
                value={administrador.phone}
                error={erros['portal_admin.phone']}
                onChange={(evento) => onAdministrador('phone', evento.target.value)}
              />
              <Select
                label="Perfil do administrador"
                placeholder="Selecione o perfil"
                options={perfis.map((perfil) => ({
                  value: perfil.id,
                  label: perfil.name,
                }))}
                value={administrador.role_id}
                error={
                  erros['portal_admin.role_ids'] ??
                  erros['portal_admin.role_id'] ??
                  erros.portal_admin
                }
                onChange={(evento) => onAdministrador('role_id', evento.target.value)}
              />
            </div>
          ) : (
            <SemConvite />
          )}
        </Secao>
      )}
    </>
  );
}

/**
 * O passo Dados do Usuario do cadastro publico (ADR 0095): o administrador do portal da
 * corretora, que entra na hora com a senha escolhida aqui.
 */
function EtapaAcesso({
  acesso,
  erros,
  avisoDePrivacidade,
  onAcesso,
}: {
  acesso: AcessoDoAssistente;
  erros: ErrosDoAssistente;
  avisoDePrivacidade: ReactNode;
  onAcesso: (campo: keyof AcessoDoAssistente, valor: string | boolean) => void;
}) {
  return (
    <Secao
      titulo="Dados do Usuario"
      descricao="A pessoa que vai administrar o portal da corretora. O acesso fica pronto ao concluir: entre com este e-mail e esta senha."
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Nome completo"
          autoComplete="name"
          value={acesso.name}
          error={erros['user.name']}
          onChange={(evento) => onAcesso('name', evento.target.value)}
        />
        <Field
          label="E-mail de acesso"
          type="email"
          autoComplete="email"
          value={acesso.email}
          error={erros['user.email']}
          onChange={(evento) => onAcesso('email', evento.target.value)}
        />
        <Field
          label="CPF"
          inputMode="numeric"
          value={acesso.cpf}
          error={erros['user.cpf']}
          onChange={(evento) => onAcesso('cpf', evento.target.value)}
        />
        <Field
          label="Telefone"
          inputMode="tel"
          autoComplete="tel"
          value={acesso.phone}
          error={erros['user.phone']}
          onChange={(evento) => onAcesso('phone', evento.target.value)}
        />
        <Field
          label="Senha"
          type="password"
          autoComplete="new-password"
          hint={`Minimo de ${SENHA_MINIMA_DO_ASSISTENTE} caracteres.`}
          value={acesso.password}
          error={erros['user.password']}
          onChange={(evento) => onAcesso('password', evento.target.value)}
        />
        <Field
          label="Confirmacao da senha"
          type="password"
          autoComplete="new-password"
          value={acesso.password_confirmation}
          error={erros['user.password_confirmation']}
          onChange={(evento) => onAcesso('password_confirmation', evento.target.value)}
        />
      </div>
      <div
        className="rounded-md border border-line bg-ink-50 p-3 text-2xs leading-relaxed text-ink-600"
        data-testid="aviso-de-privacidade"
      >
        {avisoDePrivacidade}
      </div>
      <label className="flex items-start gap-2 text-xs text-ink-700">
        <input
          type="checkbox"
          name="privacy_acknowledged"
          checked={acesso.ciente}
          onChange={(evento) => onAcesso('ciente', evento.target.checked)}
          className="mt-0.5"
        />
        Li o aviso de privacidade e estou ciente do uso dos dados.
      </label>
      {erros.privacy_acknowledged ? (
        <p className="text-2xs text-danger-600" role="alert">
          {erros.privacy_acknowledged}
        </p>
      ) : null}
    </Secao>
  );
}

/** Aviso para quem cadastra corretora sem poder convidar pessoas. */
function SemConvite() {
  return (
    <Alert tone="info">
      Voce nao pode convidar pessoas: a corretora sera cadastrada sem administrador e
      sem usuarios, e quem administra os usuarios os convida depois, pela tela de
      usuarios.
    </Alert>
  );
}

function EtapaBancarios({
  conta,
  responsavel,
  podeAlterarConta,
  bancos,
  erros,
  onConta,
  onResponsavel,
}: {
  conta: BankAccount;
  responsavel: FinancialContact;
  podeAlterarConta: boolean;
  bancos: { code: string; name: string }[] | undefined;
  erros: ErrosDoAssistente;
  onConta: (campo: keyof BankAccount, valor: string) => void;
  onResponsavel: (campo: keyof FinancialContact, valor: string) => void;
}) {
  const erroDaConta = (campo: keyof BankAccount) =>
    erros[`bank_account.${campo}`] ??
    (campo === 'bank_code' ? erros.bank_account : undefined);
  return (
    <>
      <Secao
        titulo="Dados Bancarios da Corretora"
        descricao="Inclua dados bancarios para efetuar pagamento de comissao."
      >
        {podeAlterarConta ? null : (
          <Alert tone="info">
            {dicaDadosBancarios} A corretora sera cadastrada sem a conta, e quem tem a
            permissao a informa depois, na ficha do corretor.
          </Alert>
        )}
        <fieldset disabled={!podeAlterarConta} className="flex flex-col gap-4">
          <legend className="sr-only">Conta que recebe o repasse</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <BankCodeSelect
              value={conta.bank_code}
              bancos={bancos}
              error={erroDaConta('bank_code')}
              disabled={!podeAlterarConta}
              onChange={(codigo) => onConta('bank_code', codigo)}
            />
            <Select
              label="Tipo de conta"
              placeholder="Selecione o tipo da conta"
              options={tiposDeConta}
              value={conta.account_type}
              error={erroDaConta('account_type')}
              onChange={(evento) => onConta('account_type', evento.target.value)}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-[1fr_7rem_1fr_7rem]">
            <Field
              label="Agencia"
              placeholder="Digite a agencia"
              inputMode="numeric"
              maxLength={5}
              value={conta.agency}
              error={erroDaConta('agency')}
              onChange={(evento) => onConta('agency', evento.target.value)}
            />
            <Field
              label="Digito da agencia"
              placeholder="Digito"
              maxLength={1}
              value={conta.agency_digit}
              error={erroDaConta('agency_digit')}
              onChange={(evento) => onConta('agency_digit', evento.target.value)}
            />
            <Field
              label="Conta"
              placeholder="Digite a conta"
              inputMode="numeric"
              maxLength={20}
              value={conta.account}
              error={erroDaConta('account')}
              onChange={(evento) => onConta('account', evento.target.value)}
            />
            <Field
              label="Digito da conta"
              placeholder="Digite o digito"
              maxLength={1}
              value={conta.account_digit}
              error={erroDaConta('account_digit')}
              onChange={(evento) => onConta('account_digit', evento.target.value)}
            />
          </div>
        </fieldset>
      </Secao>

      <Secao
        titulo="Dados do Responsavel Financeiro"
        descricao="Dados do responsavel financeiro da Corretora para contato da Seguradora."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Nome Completo"
            placeholder="Digite o nome completo"
            value={responsavel.name}
            error={erros['financial_contact.name']}
            onChange={(evento) => onResponsavel('name', evento.target.value)}
          />
          <Field
            label="CPF"
            placeholder="Digite o CPF"
            inputMode="numeric"
            value={responsavel.document}
            error={erros['financial_contact.document']}
            onChange={(evento) => onResponsavel('document', evento.target.value)}
          />
          <Field
            label="Email"
            type="email"
            placeholder="Digite o e-mail"
            value={responsavel.email}
            error={erros['financial_contact.email']}
            onChange={(evento) => onResponsavel('email', evento.target.value)}
          />
          <Field
            label="Celular"
            placeholder="Digite o celular"
            value={responsavel.phone}
            error={erros['financial_contact.phone']}
            onChange={(evento) => onResponsavel('phone', evento.target.value)}
          />
        </div>
      </Secao>
    </>
  );
}

function EtapaUsuarios({
  usuarios,
  perfis,
  podeConvidar,
  erros,
  onUsuario,
  onAdicionar,
  onRemover,
}: {
  usuarios: PessoaDoAssistente[];
  perfis: PerfilDoAssistente[];
  podeConvidar: boolean;
  erros: ErrosDoAssistente;
  onUsuario: (indice: number, campo: keyof PessoaDoAssistente, valor: string) => void;
  onAdicionar: () => void;
  onRemover: (indice: number) => void;
}) {
  if (!podeConvidar) {
    return (
      <Secao titulo="Dados dos Usuarios">
        <SemConvite />
      </Secao>
    );
  }
  const opcoes = perfis.map((perfil) => ({ value: perfil.id, label: perfil.name }));
  return (
    <Secao
      titulo="Dados dos Usuarios"
      descricao="Local para definir outros usuarios para uso da plataforma. Esses usuarios vao receber o convite de acesso no e-mail informado, para definir a senha."
    >
      <Alert tone="info">
        O e-mail de acesso depende do provedor de e-mail da plataforma. Enquanto ele nao
        estiver conectado, os enderecos de aceite aparecem ao concluir, para envio a
        mao.
      </Alert>
      {usuarios.length === 0 ? (
        <p className="text-sm text-ink-500">
          Clique no botao de adicionar usuarios para cadastrar um novo usuario corretor.
        </p>
      ) : (
        <ol className="flex flex-col gap-3">
          {usuarios.map((usuario, indice) => (
            <li
              key={usuario.chave}
              aria-label={`Usuario ${indice + 1}`}
              className="grid gap-3 rounded-md border border-line p-4 sm:grid-cols-[1fr_1fr_10rem_12rem_auto] sm:items-start"
            >
              <Field
                label="Nome"
                placeholder="Digite o nome"
                value={usuario.name}
                error={erros[`users[${indice}].name`]}
                onChange={(evento) => onUsuario(indice, 'name', evento.target.value)}
              />
              <Field
                label="E-mail do usuario"
                type="email"
                placeholder="Digite o e-mail"
                value={usuario.email}
                error={erros[`users[${indice}].email`]}
                onChange={(evento) => onUsuario(indice, 'email', evento.target.value)}
              />
              <Field
                label="Celular do usuario"
                placeholder="Opcional"
                value={usuario.phone}
                error={erros[`users[${indice}].phone`]}
                onChange={(evento) => onUsuario(indice, 'phone', evento.target.value)}
              />
              <Select
                label="Tipo de perfil"
                placeholder="Selecione o perfil"
                options={opcoes}
                value={usuario.role_id}
                error={
                  erros[`users[${indice}].role_ids`] ??
                  erros[`users[${indice}].role_id`] ??
                  erros[`users[${indice}]`]
                }
                onChange={(evento) => onUsuario(indice, 'role_id', evento.target.value)}
              />
              <Button
                variant="ghost"
                className="sm:mt-6"
                aria-label={`Remover usuario ${indice + 1}`}
                onClick={() => onRemover(indice)}
              >
                <Icon name="close" size={14} />
              </Button>
            </li>
          ))}
        </ol>
      )}
      <div>
        <Button variant="secondary" onClick={onAdicionar}>
          <Icon name="users" size={15} />
          Adicionar usuario
        </Button>
      </div>
    </Secao>
  );
}
